import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Wrench } from 'lucide-react';
import type { ApiResponse } from '@evi/shared';
import { api, getDisplayErrorMessage } from '@/lib/api';
import styles from './SettingsPage.module.css';

/**
 * Service calls (Warren, ruling H, 21 October 2026): how long a launched charger may be offline before a service call
 * opens to its installer. Super Admin only (Api: routes/settings/service-requests.ts); 15 to 1440 minutes; audited.
 */
const KEY = ['service-request-settings'];
export function ServiceRequestSettingsSection() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get('settings/service-requests').json<ApiResponse<{ value: { offline_minutes: number } }>>().then(r => r.data!.value), staleTime: 60_000 });
  const [minutes, setMinutes] = useState<string | null>(null), [saving, setSaving] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  const text = minutes ?? (q.data ? String(q.data.offline_minutes) : ''), dirty = q.data !== undefined && Number(text) !== q.data.offline_minutes;
  async function save() {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.put('settings/service-requests', { json: { offline_minutes: Number(text) } }).json();
      await client.invalidateQueries({ queryKey: KEY }); setMinutes(null); setSaved(true);
    } catch (err) { setError(await getDisplayErrorMessage(err, 'The setting was not saved.')); } finally { setSaving(false); }
  }
  return <section className={styles.platformFeeSection}>
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><Wrench size={16} /></div>
      <div>
        <h2 className={styles.platformFeeTitle}>Service calls</h2>
        <p className={styles.platformFeeHint}>A launched charger offline this long opens a service call to its installer (EVI ops when there is none). A
          connector Faulted for 10 minutes opens one at once. Every change is audited.</p>
      </div>
    </div>
    {q.isLoading ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p>
      : q.isError ? <p className={styles.platformFeeHint} style={{ color: 'var(--color-err)' }}>Couldn&rsquo;t load the service call setting. Refresh to retry.</p>
        : <>
          <div className={styles.platformFeeField}>
            <label className={styles.platformFeeLabel} htmlFor="service-offline-minutes">Minutes offline before a service call opens (15 to 1440)</label>
            <div className={styles.platformFeeInputRow}>
              <input id="service-offline-minutes" className={styles.platformFeeInput} type="number" min={15} max={1440} step={1}
                value={text} onChange={e => { setMinutes(e.target.value); setSaved(false); }} disabled={saving} />
              <button type="button" className={styles.platformFeeBtn} disabled={!dirty || saving} onClick={() => void save()}>
                {saving ? <><Loader2 size={13} className={styles.spin} /> Saving</> : saved && !dirty ? <><Check size={13} /> Saved</> : <>Save</>}
              </button>
            </div>
          </div>
          {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
        </>}
  </section>;
}
